import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  TextField,
  List,
  Box,
  Typography,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@billda/permissions-ui';
import { CommandPaletteResultItem } from './CommandPaletteResultItem';
import { useCommandPaletteSearch } from '../hooks/useCommandPaletteSearch';
import { useCommandPaletteKeyboard } from '../hooks/useCommandPaletteKeyboard';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const permissions = usePermissions();
  const inputRef = useRef<HTMLInputElement>(null);

  // Get search results using custom hook
  const results = useCommandPaletteSearch(query, permissions);

  // Navigation handler
  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  // Handle keyboard navigation using custom hook
  const { selectedIndex, setSelectedIndex } = useCommandPaletteKeyboard(
    open,
    results,
    handleNavigate,
    onClose
  );

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setQuery('');
      // Focus input after modal animation
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="command-palette"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '70vh',
          overflow: 'hidden',
          position: 'relative',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          bgcolor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      {/* Search Input */}
      <Box sx={{ p: 2, pb: 0 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Search navigation..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          inputProps={{
            'data-testid': 'command-palette-search-input',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: {
              height: 48,
              '& fieldset': {
                borderColor: 'divider',
              },
            },
          }}
          autoComplete="off"
        />
      </Box>

      {/* Results List */}
      <Box
        sx={{
          maxHeight: 'calc(70vh - 120px)',
          overflowY: 'auto',
          px: 2,
          pt: 1,
          pb: 2,
        }}
      >
        {results.length > 0 ? (
          <List sx={{ p: 0 }}>
            {results.map((item, index) => (
              <CommandPaletteResultItem
                key={item.path}
                item={item}
                isSelected={index === selectedIndex}
                onClick={() => handleNavigate(item.path)}
                onMouseEnter={() => setSelectedIndex(index)}
              />
            ))}
          </List>
        ) : (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No results found
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer Hints */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
          bgcolor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.02)'
              : 'rgba(0, 0, 0, 0.02)',
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}
        >
          <span>Use ↑↓ to navigate</span>
          <span>•</span>
          <span>Press Enter to select</span>
          <span>•</span>
          <span>Esc to close</span>
        </Typography>
      </Box>
    </Dialog>
  );
};
